import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { RotateCcw, Check, Ban } from "lucide-react";
import aapi, { inr, fmtDate, formatApiError } from "./aapi";
import { PageHeader, EmptyState } from "@/panel/components";
import { EASE } from "@/components/Motion";

const STYLES = {
  pending: "border-amber-400/30 bg-amber-400/10 text-amber-300",
  approved: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
  rejected: "border-rose-400/30 bg-rose-400/10 text-rose-300",
};

export default function AdminRefunds() {
  const [status, setStatus] = useState("pending");
  const [refunds, setRefunds] = useState(null);

  const load = () =>
    aapi.get("/refunds", { params: { status } }).then(({ data }) => setRefunds(data)).catch(() => setRefunds([]));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const review = async (r, action) => {
    const msg =
      action === "approve"
        ? `Approve refund of ${inr(r.amount)} to ${r.user_email}? Order ${r.order_id} becomes Refunded and the wallet is credited.`
        : `Deny the refund request for ${r.order_id}?`;
    if (!window.confirm(msg)) return;
    try {
      await aapi.post(`/refunds/${r.id}/review`, { action });
      toast.success(`Refund ${action === "approve" ? "approved — wallet credited" : "denied"} — customer notified, audit logged`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-refunds-page">
      <PageHeader title="Refund Requests" sub="Customer refund appeals — approve to credit the wallet instantly." />

      <div className="mb-6 flex flex-wrap gap-2">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`refunds-status-${s}`}
            className={`rounded-full border px-4 py-2 text-xs font-medium capitalize transition-all duration-300 ${
              status === s ? "border-gold bg-gold/10 text-goldlight" : "border-white/10 text-neutral-400 hover:border-gold/40"
            }`}>
            {s}
          </button>
        ))}
      </div>

      {refunds === null ? (
        <div className="flex justify-center py-24">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/20 border-t-gold" />
        </div>
      ) : refunds.length === 0 ? (
        <EmptyState icon={RotateCcw} title="No refund requests" sub="Customer appeals will queue here for review." testid="refunds-empty" />
      ) : (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
          className="space-y-3" data-testid="refunds-list">
          {refunds.map((r) => (
            <div key={r.id} data-testid={`refund-row-${r.id}`}
              className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-white/10 bg-panel/80 px-6 py-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-semibold text-neutral-100">{inr(r.amount)}</span>
                  <span className="font-mono text-xs text-gold/80">{r.order_id}</span>
                  <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STYLES[r.status]}`}>
                    {r.status}
                  </span>
                </div>
                <div className="mt-1.5 truncate text-xs text-neutral-400" title={r.service_name}>{r.service_name}</div>
                <div className="mt-1 text-xs text-neutral-500">Reason: {r.reason}</div>
                <div className="mt-1 font-mono text-[11px] text-neutral-600">
                  {r.user_email} · {fmtDate(r.created_at)}
                </div>
              </div>
              {r.status === "pending" && (
                <div className="flex gap-2">
                  <button onClick={() => review(r, "approve")} data-testid={`refund-approve-${r.id}`}
                    className="flex items-center gap-1.5 rounded-full bg-gradient-to-br from-goldlight to-golddark px-4 py-2 text-xs font-bold text-black">
                    <Check size={13} /> Approve & Refund
                  </button>
                  <button onClick={() => review(r, "deny")} data-testid={`refund-deny-${r.id}`}
                    className="flex items-center gap-1.5 rounded-full border border-rose-400/40 px-4 py-2 text-xs font-semibold text-rose-300 transition-colors hover:bg-rose-400/10">
                    <Ban size={13} /> Deny
                  </button>
                </div>
              )}
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
}
